import type { FC, ReactElement } from 'react';

import type { Classnames, IconColor, IconList, StatusTheme } from '@snowit/icetypes';

import useFloatComponent from '@snowit/hooks/useFloat/useFloatComponent';
import { Typo } from '@snowit/ui';
import testId from '@snowit/utils/testId';

import BadgeLabel from '../Badges/BadgeLabel';
import Icon from '../Icon';

import cx from 'classnames';
import s from './Status.module.scss';

export interface IStatus {
  theme?: StatusTheme;
  status: string;
  // status: StatusType;
  custom?: {
    color: string;
    icon: IconList;
  };
  label?: string;
  tooltip?: string;
  id?: string;
  classNames?: Classnames<'wrapper' | 'icon' | 'label' | 'badge' | 'tooltip' | 'hintTooltip'>;
  test?: string;
}

const Status: FC<IStatus> = (props: IStatus): ReactElement => {
  const { theme = 'icon', status, custom, label, tooltip, id, classNames, test } = props;
  const { Float } = useFloatComponent();

  const getStatus = (): { color: string; icon: IconList } => {
    if (custom) {
      return { color: custom.color, icon: custom.icon };
    }
    switch (status) {
      case 'info': // square - quadrato pieno
        return { color: 'var(--c-info)', icon: 'square' };
      case 'last':
      case 'error': // square - quadrato pieno
        return { color: 'var(--c-error)', icon: 'square' };
      case 'rejected': // square_2 - quadrato bordo
        return { color: 'var(--c-error)', icon: 'square_2' };
      case 'last_units':
        return { color: 'var(--c-ok)', icon: 'square_4' };
      case 'few':
      case 'warning': // triangle - triangolo pieno
        return { color: 'var(--c-warning)', icon: 'triangle' };
      case 'processing': // triangle_2 - triangolo bordo
        return { color: 'var(--c-warning)', icon: 'triangle_2' };
      case 'pending': // triangle_dot - triangolo rovesciato
        return { color: 'var(--c-warning)', icon: 'triangle_3' };
      case 'almost_ok':
        return { color: 'var(--c-ok)', icon: 'circle_2' };
      case 'available':
      case 'ok': // circle - cerchio pieno
        return { color: 'var(--c-ok)', icon: 'circle' };
      case 'worn':
        return { color: 'var(--c-bg-d3)', icon: 'square_3' };
      default:
        return { color: 'var(--c-bg-d3)', icon: 'circle' };
    }
  };
  const { icon, color } = getStatus();

  const renderBadge = () => {
    if (theme !== 'badge') return null;
    return (
      <BadgeLabel
        className={classNames?.badge}
        label={label || status}
        icon={icon}
        theme={'color'}
        bgColor={color}
        labelColor={'var(--c-white)'}
        size={'sm'}
      />
    );
  };

  const renderIcon = () => {
    if (theme === 'badge') return null;
    return (
      <Icon
        className={cx(s.icon, classNames?.icon)}
        name={icon}
        color={color as IconColor}
        a11y={{ label: label ?? status }}
      />
    );
  };

  const renderLabel = () => {
    if (theme !== 'label') return null;
    return (
      <Typo
        className={cx(s.label, classNames?.label)}
        text={label || ''}
        size={'md'}
        color={'text'}
      />
    );
  };

  const renderTooltip = () => {
    if (!tooltip) return null;
    return (
      <div className={cx(s.tooltip, classNames?.tooltip)}>
        <Typo className={cx(s.hintTooltip, classNames?.hintTooltip)} text={tooltip} size={'sm'} />
      </div>
    );
  };

  return (
    <Float id={'status-badge'} offset={[0, 8]} content={renderTooltip()} strategy={'click'}>
      <div
        className={cx(s.wrapper, classNames?.wrapper, {
          [s[`${theme}Theme`]]: true,
        })}
        id={id}
        {...testId(test)}
      >
        {renderBadge()}
        {renderIcon()}
        {renderLabel()}
      </div>
    </Float>
  );
};

export default Status;
